// ABOUTME: Ownership check step - asks if user has an internet sculpture
// ABOUTME: Has two states: ask (initial) and not-yet (shows shop link)

import { motion, AnimatePresence } from "framer-motion";
import type { OwnershipState } from "./Onboarding";

interface OwnershipStepProps {
  state: OwnershipState;
  onYes: () => void;
  onNotYet: () => void;
  onContinueAnyway: () => void;
  onSkip: () => void;
}

// External link icon
function ExternalLinkIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

export function OwnershipStep({
  state,
  onYes,
  onNotYet,
  onContinueAnyway,
  onSkip,
}: OwnershipStepProps) {
  return (
    <div className="onboarding-step ownership-step">
      {/* Hero area with objects */}
      <motion.div
        className="ownership-step__hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.img
          src="/objects/fortune-cookie.png"
          alt="Fortune Webstone"
          className="ownership-step__object ownership-step__object--1"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        />
        <motion.img
          src="/objects/pillow.png"
          alt="Phone Pillow"
          className="ownership-step__object ownership-step__object--2"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        />
      </motion.div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {state === "ask" ? (
          <motion.div
            key="ask"
            className="onboarding-step__content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h2 className="onboarding-step__title">
              Do you have an internet sculpture?
            </h2>
          </motion.div>
        ) : (
          <motion.div
            key="not-yet"
            className="onboarding-step__content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h2 className="onboarding-step__title">
              Find your perfect sculpture
            </h2>
            <p className="onboarding-step__subtitle">
              Internet sculptures are interactive objects that introduce
              whimsical everyday rituals into your home.
            </p>

            {/* Shop link */}
            <a
              href="https://internetsculptures.com"
              target="_blank"
              rel="noopener noreferrer"
              className="onboarding-step__shop-link"
            >
              Visit the shop
              <ExternalLinkIcon />
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fixed bottom actions */}
      <motion.div
        className="onboarding-step__footer"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        {state === "ask" ? (
          <>
            <button className="onboarding-step__button" onClick={onYes}>
              Yes, I have one
            </button>
            <button
              className="onboarding-step__button onboarding-step__button--secondary"
              onClick={onNotYet}
            >
              Not yet
            </button>
          </>
        ) : (
          <button
            className="onboarding-step__button"
            onClick={onContinueAnyway}
          >
            Continue Anyway
          </button>
        )}
        <button className="onboarding-step__skip" onClick={onSkip}>
          skip for now
        </button>
      </motion.div>
    </div>
  );
}
